import { forwardRef, type ButtonHTMLAttributes } from "react";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Variante visuelle.
   * - `primary` : action principale (or champagne)
   * - `secondary` : action secondaire (gris foncé)
   * - `ghost` : action discrète (transparent + hover)
   * - `danger` : action destructive (rust — supprimer, exclure, irréversible)
   */
  variant?: "primary" | "secondary" | "ghost" | "danger";
  /**
   * Taille du bouton. `tactile` est le défaut sur écrans terrain (mains mouillées).
   * - `default` : 40px de hauteur (desktop classique)
   * - `tactile` : 48px de hauteur (cible tactile WCAG min)
   * - `confort` : 56px de hauteur (cible confort mains mouillées / engourdies)
   */
  size?: "default" | "tactile" | "confort";
}

// Variantes alignées sur la charte (`docs/design/charte-graphique.md` §4) :
// - primary   : action principale (gold mat, jamais gradient saturé)
// - secondary : action alternative (outline marron)
// - ghost     : action discrète sur fond crème (transparent + hover crème)
const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary:
    "bg-champagne-600 text-white shadow-sm hover:bg-champagne-700 focus-visible:ring-champagne-300",
  secondary:
    "border border-champagne-200 bg-white text-champagne-900 hover:border-champagne-500 hover:text-champagne-700 focus-visible:ring-champagne-300",
  ghost:
    "bg-transparent text-champagne-900 hover:bg-champagne-50 hover:text-champagne-700 focus-visible:ring-champagne-300",
  danger: "bg-rust-600 text-white shadow-sm hover:bg-rust-700 focus-visible:ring-rust-300",
};

const sizeClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
  default: "h-10 px-4 text-sm",
  tactile: "h-12 px-5 text-base",
  confort: "h-14 px-6 text-lg",
};

/**
 * Bouton standard champ-erp.
 * Convention : par défaut taille `tactile` (48px) pour rester utilisable au
 * pressoir. Border-radius `rounded-xl` (12px) — charte §4 interdit `rounded-md`
 * (trop institutionnel) et `rounded-full` (trop SaaS-startup).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "tactile", className = "", children, ...props },
  ref,
) {
  const baseClasses =
    "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-4";

  return (
    <button
      ref={ref}
      className={[baseClasses, variantClasses[variant], sizeClasses[size], className]
        .filter(Boolean)
        .join(" ")}
      {...props}
    >
      {children}
    </button>
  );
});
